import { Metric } from '@fullstory/proto/fs/services/search-settings';
import { Event, NativePlatform } from '@fullstory/proto/fs/api/segment';
import { FormattedMessage } from 'react-intl';
import React, { useCallback, useContext } from 'react';
import { Button, Flex } from '@fullstory/components';
import { FsEvent } from '@fullstory/fs-script-api';
import { useOptimizeUtil } from '../../hooks/useOptimizeUtil';
import { AppearancesContext, PreviewContext } from '@fullstory/signal-studio-context';
import { serializeSelector as serializeSelectorTree } from '../../utils/selector-tree';
import { makePlatformSelectorDeps } from '../../utils';
import { SelectorRule } from '@fullstory/selectors';
import { fixSelector } from '../../utils/optimize/fixSelector';
import { useFlags } from '@fullstory/react-hooks';

interface SelectorTrimButtonProps {
  selector: string;
  platforms: NativePlatform[];
  onStart?: () => void;
  onFinish: (selector: string, error?: string) => void;
  onPartial?: (selector: string) => void;
  trimTrigger?: boolean;
  setTrimTrigger?: (off: boolean) => void;
  abortController: AbortController;
  setAbortController: (controller: AbortController) => void;
  trimming?: boolean;
}

export const SelectorFixButton = ({
  selector,
  platforms,
  onStart,
  onFinish,
  onPartial = () => {},
  abortController,
  setAbortController,
  trimming,
}: SelectorTrimButtonProps) => {
  const appearances = useContext(AppearancesContext);
  const getMetricFn = useCallback(
    (selector: string): Metric | null => {
      if (!SelectorRule.isValid(selector) || !appearances) {
        return null;
      }
      const event: Event = {
        primary: null,
        deps: makePlatformSelectorDeps({ selector, platforms }),
      };
      return appearances.getMetricFn({ event });
    },
    [appearances, platforms],
  );
  const { rendererClient } = useContext(PreviewContext);

  const { fetchTotalVolume } = useOptimizeUtil(getMetricFn, rendererClient);

  const fix = useCallback(async () => {
    if (!selector) {
      return;
    }

    const newAbortController = new AbortController();
    setAbortController(newAbortController);
    onStart?.();

    const abortableValidate = async (s1: string) => {
      if (newAbortController.signal.aborted) {
        throw new Error('Aborted');
      }

      const volume = await fetchTotalVolume(s1);
      return volume > 0;
    };

    try {
      const fixedSelector = await fixSelector(selector, abortableValidate, onPartial);

      FsEvent.recordCustomEventV2('selector-debug', {
        startSelector: selector,
        endSelector: fixedSelector,
      });

      const serializedSelector = serializeSelectorTree(fixedSelector);
      onFinish(serializedSelector);
    } catch (e) {
      onFinish(selector);
    }
  }, [fetchTotalVolume, onFinish, onPartial, onStart, selector, setAbortController]);

  const abort = useCallback(() => {
    abortController.abort();
  }, [abortController]);

  const newFSTAColors = useFlags().featureFlags().AppNewFstaColors;

  if (!appearances) return null;

  return (
    <Flex data-component="SelectorTrimButton" justifyContent="space-between" gap={8}>
      <Button
        iconLeft={trimming ? 'ai-animated' : 'ai'}
        variant={newFSTAColors ? 'outline-intelligence-gradient' : 'outline-intelligence'}
        size="small"
        onClick={trimming ? abort : fix}
      >
        {trimming ? (
          <FormattedMessage defaultMessage="Cancel" id="47FYwb" />
        ) : (
          <FormattedMessage defaultMessage="Debug" id="z3UjXR" />
        )}
      </Button>
    </Flex>
  );
};
